import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectCards } from 'swiper/modules'

import CompanyCard from './CompanyCard'
import CrossSpotlightIcon from '../../../../../assets/svgs/CrossSpotlight'
import useWindowWidth from '../../../../../hooks/useWindowWidth'
import { compactNumber } from '../../format'

import style from './style.module.css'
import 'swiper/css'
import 'swiper/css/effect-cards'
import './style.css'

/**
 * Stable keys for the first-page skeletons. The array index would be fine here
 * (the placeholders carry no identity and never reorder) but naming them keeps
 * the "no index keys" rule true everywhere in this folder.
 */
const SKELETON_KEYS = ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5', 'sk-6']

/**
 * Mirrors the real card's three-column anatomy — two 85x185 white side panels
 * either side of a 140px middle column — so the grid does not jump when the data
 * lands.
 */
const SkeletonCard = () => (
  <div className={style.skeletonCard} aria-hidden="true">
    <div className={style.skeletonCard_side} />

    <div className={style.skeletonCard_middle}>
      <div className={style.skeletonCard_logo} />
      <div className={style.skeletonCard_line} />
      <div className={`${style.skeletonCard_line} ${style.skeletonCard_line_short}`} />
      <div className={style.skeletonCard_illustration} />
    </div>

    <div className={style.skeletonCard_side} />
  </div>
)

const Companies = ({
  companies,
  totalCount,
  loading,
  loadingMore,
  hasMore,
  error,
  notice,
  activeFilterCount,
  onLoadMore,
  onOpenCompany,
  onToggleSave,
  onClearFilters,
  onDismissNotice,
  onRetry,
}) => {
  const isMobile = useWindowWidth() <= 768

  const list = companies || []
  const total = Number.isFinite(Number(totalCount)) ? Number(totalCount) : list.length

  /*
   * A non-blocking failure: a "load more" that did not come back, a bookmark the
   * server rejected. It sits above whatever is on screen and never replaces it,
   * because the rows already loaded are still perfectly usable.
   */
  const noticeStrip = notice ? (
    <div className={style.notice} role="status">
      <span className={style.notice_text}>{notice}</span>
      <button
        type="button"
        className={style.notice_close}
        onClick={() => onDismissNotice?.()}
        aria-label="Dismiss message"
        data-log-action="spotlight_dismiss_notice"
      >
        <CrossSpotlightIcon />
      </button>
    </div>
  ) : null

  // ---- first page loading ------------------------------------------------
  if (loading && !list.length) {
    return (
      <div className={style.companiesWrapper}>
        <p className={style.resultsLine} role="status">
          Loading companies…
        </p>
        {noticeStrip}
        <div className={style.skeletonGrid}>
          {SKELETON_KEYS.map((key) => (
            <SkeletonCard key={key} />
          ))}
        </div>
      </div>
    )
  }

  // ---- hard failure ------------------------------------------------------
  // A load-more failure leaves rows on screen and surfaces as a notice, so the
  // full-bleed panel is only for the case where there is nothing at all to look
  // at.
  if (error && !list.length) {
    return (
      <div className={style.companiesWrapper}>
        <div className={style.statePanelWrap}>
          <div className={style.statePanel} role="alert">
            <span className={style.statePanel_title}>Could not load companies</span>
            <span className={style.statePanel_text}>{error.message}</span>
            <button
              type="button"
              className={style.statePanel_action}
              onClick={onRetry}
              data-log-action="spotlight_retry"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ---- empty -------------------------------------------------------------
  if (!list.length) {
    const filtered = activeFilterCount > 0

    return (
      <div className={style.companiesWrapper}>
        {noticeStrip}
        <div className={style.statePanelWrap}>
          <div className={style.statePanel}>
            <span className={style.statePanel_title}>
              {filtered ? 'No companies match these filters' : 'No companies to show yet'}
            </span>
            <span className={style.statePanel_text}>
              {filtered
                ? 'Try removing a filter or two — the search box and the rail both narrow the list.'
                : 'Companies appear here as soon as we have activity to show for them.'}
            </span>
            {filtered && (
              <button
                type="button"
                className={style.statePanel_action}
                onClick={onClearFilters}
                data-log-action="spotlight_clear_filters"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ---- list --------------------------------------------------------------
  return (
    <div className={style.companiesWrapper}>
      <p className={style.resultsLine} role="status">
        <strong>{compactNumber(total)}</strong> {total === 1 ? 'company' : 'companies'}
      </p>

      {noticeStrip}

      {isMobile ? (
        <Swiper
          effect={'cards'}
          grabCursor={true}
          modules={[EffectCards]}
          className={style.companiesSwiper}
        >
          {list.map((company) => (
            <SwiperSlide key={company.orgKey}>
              <CompanyCard
                company={company}
                onOpen={onOpenCompany}
                onToggleSave={onToggleSave}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className={style.companies}>
          {list.map((company) => (
            <CompanyCard
              key={company.orgKey}
              company={company}
              onOpen={onOpenCompany}
              onToggleSave={onToggleSave}
            />
          ))}
        </div>
      )}

      {(hasMore || error) && (
        <div className={style.footer}>
          {/* A paging failure keeps the rows that already loaded; say so without
              tearing the list down. */}
          {error && <span className={style.footer_error}>{error.message}</span>}

          {hasMore && (
            <>
              <button
                type="button"
                className={style.loadMore}
                onClick={onLoadMore}
                disabled={loadingMore}
                data-log-action="spotlight_load_more"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
              <span className={style.footer_count}>
                Showing {compactNumber(list.length)} of {compactNumber(total)}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default Companies
